import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import api from "../services/api";

export default function Booking() {
  // -------------------
  // HEADER
  // -------------------
  const [bookingHeader, setBookingHeader] = useState({
    booking: "",
    edate: "",
    rdate: "",
    ddate: ""
  });
  const [bookingId, setBookingId] = useState(null);

  // -------------------
  // LINES
  // -------------------
  const [poRows, setPoRows] = useState([]);
  const [lines, setLines] = useState([]);
  const [lineForm, setLineForm] = useState({ PO: "", item: "" });

  // -------------------
  // SEARCH
  // -------------------
  const [bookingSearchRows, setBookingSearchRows] = useState([]);
  const [searchBooking, setSearchBooking] = useState(null);

  // -------------------
  // LOAD PO OPTIONS
  // -------------------
  const loadPO = async () => {
    try {
      const res = await api.get("/booking/lookup/po");
      setPoRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setPoRows([]);
    }
  };

  // -------------------
  // LOAD BOOKINGS FOR SEARCH
  // -------------------
  const loadBookingsForSearch = async () => {
    try {
      const res = await api.get("/booking/search");
      setBookingSearchRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setBookingSearchRows([]);
    }
  };

  useEffect(() => {
    loadPO();
    loadBookingsForSearch();
  }, []);

  // -------------------
  // RESET (start a fresh booking without reloading the page)
  // -------------------
  const resetBooking = () => {
    setBookingHeader({ booking: "", edate: "", rdate: "", ddate: "" });
    setBookingId(null);
    setLines([]);
    setLineForm({ PO: "", item: "" });
    setSearchBooking(null);
  };

  // -------------------
  // SAVE HEADER
  // -------------------
  const saveHeader = async () => {
    if (!bookingHeader.booking) return alert("Booking number required");

    try {
      let savedId;
      if (bookingId) {
        await api.put(`/booking/header/${bookingId}`, bookingHeader);
        savedId = bookingId;
      } else {
        const res = await api.post("/booking/header", bookingHeader);
        savedId = res.data.BookingID;
        setBookingId(savedId);
      }
      // Refresh both lookups so newly-added sales orders show up immediately.
      await Promise.all([loadPO(), loadBookingsForSearch()]);
      alert(bookingId ? "Booking Header Updated" : "Booking Header Saved. Now add POs.");
    } catch (err) {
      console.error(err);
      alert("Failed to save booking header");
    }
  };

  // -------------------
  // ADD LINE
  // -------------------
  const addLine = async () => {
    if (!bookingId) return alert("Save booking header first");
    if (!lineForm.PO) return alert("Select PO");

    try {
      const res = await api.post("/booking/line", { BookingID: bookingId, ...lineForm });
      setLines((prev) => [...prev, { ...res.data, PO: lineForm.PO, item: lineForm.item }]);
      setLineForm({ PO: "", item: "" });
      await Promise.all([loadPO(), loadBookingsForSearch()]);
    } catch (err) {
      console.error(err);
      alert("Failed to add PO");
    }
  };

  // -------------------
  // DELETE LINE
  // -------------------
  const deleteLine = async (lineId) => {
    try {
      await api.delete(`/booking/line/${lineId}`);
      setLines(lines.filter(l => l.LineID !== lineId));
      await Promise.all([loadPO(), loadBookingsForSearch()]);
    } catch (err) {
      console.error(err);
    }
  };

  // -------------------
  // LOAD LINES FOR BOOKING
  // -------------------
  const loadBookingLines = async (booking) => {
    try {
      const res = await api.get(`/booking/${booking.BookingID}/lines`);
      if (Array.isArray(res.data)) {
        // Map lines to include PO and Product
        const mappedLines = res.data.map(l => ({
          ...l,
          PO: l.PO,
          item: l.item || ""
        }));
        setLines(mappedLines);
      } else {
        setLines([]);
      }

      // Update header
      setBookingHeader({
        booking: booking.BookingNo || "",
        edate: booking.ERDDate ? String(booking.ERDDate).split("T")[0] : "",
        rdate: booking.DOCCutOff ? String(booking.DOCCutOff).split("T")[0] : "",
        ddate: booking.CargoCutOff ? String(booking.CargoCutOff).split("T")[0] : ""
      });

      setBookingId(booking.BookingID);
    } catch (err) {
      console.error(err);
      setLines([]);
    }
  };

  return (
    <Box p={2}>
      {/* HEADER */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Booking Header</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Booking Number"
              fullWidth
              value={bookingHeader.booking}
              onChange={e => setBookingHeader({ ...bookingHeader, booking: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              type="date"
              label="ERD Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bookingHeader.edate}
              onChange={e => setBookingHeader({ ...bookingHeader, edate: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              type="date"
              label="DOC CUT OFF"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bookingHeader.rdate}
              onChange={e => setBookingHeader({ ...bookingHeader, rdate: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              type="date"
              label="CARGO CUT OFF"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={bookingHeader.ddate}
              onChange={e => setBookingHeader({ ...bookingHeader, ddate: e.target.value })}
            />
          </Grid>
        </Grid>
        <Box mt={2} sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button variant="contained" onClick={saveHeader}>
            {bookingId ? "Update Booking Header" : "Save Booking Header"}
          </Button>
          <Button variant="outlined" onClick={resetBooking}>
            New Booking
          </Button>
          {bookingId && (
            <Typography variant="body2" color="text.secondary">
              Editing booking #{bookingHeader.booking} (BookingID: {bookingId})
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ADD LINE */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Add POs</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={poRows}
              getOptionLabel={o => o?.SO ? `${o.SO} - ${o.Item_Description || ""}` : ""}
              value={poRows.find(p => p.SO === lineForm.PO) || null}
              onChange={(e, v) => setLineForm({ PO: v?.SO || "", item: v?.Item_Description || "" })}
              renderInput={params => <TextField {...params} label="Customer PO" fullWidth />}
              fullWidth
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField label="Product" fullWidth value={lineForm.item} InputProps={{ readOnly: true }} />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button fullWidth variant="contained" onClick={addLine}>Add PO</Button>
          </Grid>
        </Grid>

        {/* LINES TABLE */}
        <TableContainer sx={{ mt: 2, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(l => (
                <TableRow key={l.LineID}>
                  <TableCell>{l.PO}</TableCell>
                  <TableCell>{l.item}</TableCell>
                  <TableCell>
                    <Button size="small" color="error" onClick={() => deleteLine(l.LineID)}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* SEARCH */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Search Booking</Typography>
        <Autocomplete
          options={bookingSearchRows}
          getOptionLabel={b => b.BookingNo || ""}
          value={searchBooking}
          onChange={(e, v) => {
            setSearchBooking(v);
            if (v) loadBookingLines(v);
          }}
          renderInput={params => <TextField {...params} label="Booking Number" />}
        />
      </Paper>
    </Box>
  );
}
